import React, { useEffect } from 'react'
import { Dispatch } from 'redux'
import { Formik } from 'formik'
import { IGroup } from '../../types/group'
import { PackType } from '../../types/pack'
import { ICapabilitiesSummary } from '../../types/capabilitiesSummary'
import { getGroups } from '../../actions/groups'
import { getPackById, cleanupPack, updatePack } from '../../actions/pack'
import { getCapabilitiesSummary } from '../../actions/capabilitiesSummary'
import {
  extractEditionById,
  prohibitedTab,
  getFirstEditionIdFromPack,
} from '../../services/pack'
import {
  transformPackStateToInput,
  transformPackInputToState,
} from '../../services/packForm'
import { getEdition } from '../../services/views'
import { packPageTabs } from './config'
import { GreenThemeProvider } from '../../styles'
import { Main, HeightAdjuster } from '../../layouts'
import { UpdatePackForm, CreateDataFileForm } from '../../forms'
import { packValidationSchema } from '../../forms/PackForm/validation'
import {
  GraphicSidebar,
  View,
  TabsHead,
  SlugValidateModal,
  Breadcrumbs,
} from '../../components'
import { PublishScheduleProvider } from '../../components/AddPublishScheduleArea'
import { PackTitle } from './PackTitle'
import { PackDetailsTab } from './PackDetailsTab'
import { PackUploadTab } from './PackUploadTab'
import { EditionUploadTab } from './EditionUploadTab'
import { FileDetailsTab } from './FileDetailsTab'
import { PackHistoryTab } from './PackHistoryTab'
import { EditionRevisionsTab } from './EditionRevisionsTab'
import { IGroupParam, IPackParam } from '../../types/router'
import { RouteComponentProps } from 'react-router'

export interface IPackPageConnectedProps {
  pack: PackType
  capabilitiesSummary?: ICapabilitiesSummary
  isLightGroup: boolean
  group?: IGroup
  groupsLoaded: boolean
  groupsLoading: boolean
  dispatch: Dispatch
}

export const PackPage = ({
  match: {
    params: { groupId, packId },
  },
  location,
  pack,
  capabilitiesSummary,
  isLightGroup,
  group,
  groupsLoaded,
  groupsLoading,
  dispatch,
}: IPackPageConnectedProps & RouteComponentProps<IGroupParam & IPackParam>) => {
  useEffect(() => {
    dispatch(getPackById(packId, groupId))
  }, [packId, groupId, dispatch])

  useEffect(() => {
    dispatch(getCapabilitiesSummary(groupId))
  }, [dispatch, groupId])

  useEffect(() => {
    // Cleaning up pack data for canceling polling
    return () => {
      dispatch(cleanupPack())
    }
  }, [dispatch])

  useEffect(() => {
    if (!groupsLoaded && !groupsLoading) {
      dispatch(getGroups())
    }
  })

  const editionIdFromURL = getEdition(location)
  const editionId = isLightGroup
    ? editionIdFromURL === 'overall-pack'
      ? getFirstEditionIdFromPack(pack)
      : editionIdFromURL
    : editionIdFromURL
  const edition = extractEditionById(editionId)(pack)

  return (
    <GreenThemeProvider>
      <Formik
        key={pack.modified}
        initialValues={transformPackStateToInput(pack)}
        onSubmit={(values) => {
          dispatch(updatePack(transformPackInputToState(values)))
        }}
        render={(formikProps) => (
          <Main
            header={group && <Breadcrumbs group={group} />}
            sidebarSecondary={
              isLightGroup ? null : (
                <GraphicSidebar
                  pack={pack}
                  editionId={editionId}
                  formikProps={formikProps}
                />
              )
            }
            titleBlock={
              <PackTitle
                editionId={editionId}
                pack={pack}
                isLightGroup={isLightGroup}
              />
            }
            helmet={
              group &&
              `Reuters Graphics: ${group.shortTitle}: ${
                pack.title || 'Untitled'
              }`
            }
          >
            {editionId !== 'add-datafile' ? (
              <TabsHead
                tabs={packPageTabs.filter(
                  ({ value }) => !prohibitedTab(value, edition)
                )}
              />
            ) : null}
            <HeightAdjuster
              shrink={editionId === 'add-datafile' ? '0px' : '65px'}
            >
              <PublishScheduleProvider>
                <View tab="details" className="pack-page-tab-view">
                  {editionId === 'overall-pack' ? (
                    <PackDetailsTab pack={pack} />
                  ) : (
                    <FileDetailsTab
                      editionId={editionId}
                      pack={pack}
                      isLightGroup={isLightGroup}
                    />
                  )}
                </View>
                <View keep tab="upload" className="pack-page-tab-view">
                  {editionId === 'overall-pack' ? (
                    <PackUploadTab
                      pack={pack}
                      capabilitiesSummary={capabilitiesSummary}
                      formikProps={formikProps}
                    />
                  ) : (
                    editionId === 'add-datafile' && (
                      <CreateDataFileForm
                        pack={pack}
                        packFormDirty={formikProps.dirty}
                      />
                    )
                  )}
                  {pack.editions.map((item) => (
                    <View
                      keep
                      edition={isLightGroup ? 'overall-pack' : item.id}
                      key={item.id}
                      className="edition-wrapper-view"
                    >
                      <EditionUploadTab
                        edition={item}
                        pack={pack}
                        formikProps={formikProps}
                      />
                    </View>
                  ))}
                </View>
                <View keep tab="publish" className="pack-page-tab-view">
                  <UpdatePackForm
                    editionId={editionId}
                    pack={pack}
                    formikProps={formikProps}
                    isLightGroup={isLightGroup}
                  />
                  <SlugValidateModal />
                </View>
                <View tab="revisions" className="pack-page-tab-view">
                  {!pack.isLoading && <EditionRevisionsTab edition={edition} />}
                </View>
                <View tab="history" className="pack-page-tab-view">
                  {!pack.isLoading && (
                    <PackHistoryTab
                      editionId={
                        editionId === 'overall-pack' ? undefined : editionId
                      }
                      packId={packId}
                    />
                  )}
                </View>
              </PublishScheduleProvider>
            </HeightAdjuster>
          </Main>
        )}
        validationSchema={packValidationSchema}
      />
    </GreenThemeProvider>
  )
}
